/**
 * PO Line Detail Tab - extracted from POLineDetailModal.
 *
 * Renders the main detail view (left column: line fields, right column: changelog).
 */
import React, { useMemo } from 'react';
import { Calendar, Hash, DollarSign } from 'lucide-react';
import type { PurchaseOrderLine } from '../../lib/api';
import { formatExchangeRateDisplay } from '../../lib/exchangeRateDisplay';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { ReadOnlyField, DocumentLinksField } from './POLineDetailFields';
import ContractPriceDetailModal from './ContractPriceDetailModal';
import { collapseChangeRequestRows } from './auditHistoryCollapse';
import {
  getContractPriceVariance, hasContract, varianceTextClass,
  NO_CONTRACT_TEXT, LOT_NOT_APPLICABLE_TEXT,
} from './contractPriceVariance';

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

/** Customer review 11 Sep (item 27b): what each `exchange_rate_source` means to a buyer. */
const EXCHANGE_RATE_SOURCE_LABELS: Record<string, string> = {
  po_conversion: 'rate used to convert this order',
  erp_db_rate: 'ERP rate behind the converted values on the dashboards',
  erp_order_rate: 'ERP rate at order time',
};

/** Status badge configuration matching old portal display mapping. */
export const getAcknowledgeStatusBadge = (status?: string) => {
  if (!status) return null;

  const statusConfig: Record<string, { label: string; className: string }> = {
    new: { label: 'New', className: 'bg-blue-500 text-white' },
    declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
    change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
    change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
    wip: { label: 'WIP', className: 'bg-indigo-200 text-indigo-900' },
    accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
    despatched: { label: 'Dispatched', className: 'bg-gray-200 text-gray-900' },
    change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
    cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-200 text-gray-900' };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

// CR-3403: Interface for pending change
export interface PendingChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  reason?: string;
  changeDate: string;
  changedBy: string;
  icon: React.ReactNode;
}

/** Get pending changes for review based on acknowledge_status. */
export function getPendingChanges(
  line: PurchaseOrderLine,
  changelogs: ChangelogEntry[],
  canShowWorkflow: boolean,
  userPlatform?: string,
  currentUserId?: number,
): PendingChange[] {
  if (!canShowWorkflow) return [];

  const pendingChanges: PendingChange[] = [];
  const status = line.acknowledge_status;

  // CR-4688: If acknowledge_status is no longer change_proposed_*, all
  // proposals have been fully processed — no pending changes to show.
  if (status !== 'change_proposed_supplier' && status !== 'change_proposed_customer') return [];

  const recentChanges = changelogs.filter(log => {
    const changeDate = new Date(log.change_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return changeDate >= thirtyDaysAgo;
  });

  const latestByField: Record<string, ChangelogEntry> = {};
  for (const log of recentChanges) {
    const field = log.field_changed;
    if (!latestByField[field] || new Date(log.change_date) > new Date(latestByField[field].change_date)) {
      latestByField[field] = log;
    }
  }

  const fieldConfig: Record<string, { label: string; icon: React.ReactNode }> = {
    delivery_date: { label: 'Customer Required Date', icon: <Calendar className="h-5 w-5" /> },
    supplier_promise_date: { label: 'Promise Date', icon: <Calendar className="h-5 w-5" /> },
    quantity: { label: 'Quantity', icon: <Hash className="h-5 w-5" /> },
    unit_price: { label: 'Unit Price', icon: <DollarSign className="h-5 w-5" /> },
  };

  for (const [field, config] of Object.entries(fieldConfig)) {
    const log = latestByField[field];
    if (log && log.old_value && log.new_value) {
      const isProposedChange = log.reason?.toLowerCase().includes('proposed') ||
        status === 'change_proposed_supplier' ||
        status === 'change_proposed_customer';

      if (isProposedChange) {
        // Only show changes from the OTHER party
        // 1. Skip if changed_by is the current user (you don't accept your own proposals)
        if (currentUserId && Number(log.changed_by) === currentUserId) continue;
        // 2. Check company_type on the changelog entry
        const changeCompanyType = (log as unknown as Record<string, unknown>)['company_type'] as string | undefined;
        const isCustomer = userPlatform === 'customer' || userPlatform === 'CUSTOMER' || userPlatform === 'HELIX';
        if (isCustomer && changeCompanyType === 'customer') continue;
        if (!isCustomer && changeCompanyType === 'supplier') continue;

        pendingChanges.push({
          field,
          label: config.label,
          oldValue: log.old_value,
          newValue: log.new_value,
          reason: log.reason,
          changeDate: log.change_date,
          changedBy: log.changed_by,
          icon: config.icon,
        });
      }
    }
  }

  return pendingChanges;
}

/** Get proposer name based on status. */
export function getProposerInfo(line: PurchaseOrderLine) {
  const status = line.acknowledge_status;
  if (status === 'change_proposed_supplier') {
    return { name: line.supplier_name || 'Supplier', type: 'supplier' };
  } else if (status === 'change_proposed_customer') {
    return { name: line.customer_name || 'Customer', type: 'customer' };
  }
  return { name: 'System', type: 'system' };
}

/* ------------------------------------------------------------------ */
/*  Detail Tab Component                                               */
/* ------------------------------------------------------------------ */

interface POLineDetailTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'customer' | 'supplier';
  userId?: number;
  formatCurrency: (value: string, currency?: string) => string;
  formatDate: (dateString: string) => string;
  /** CR-7262: quantities keep up to 3 decimals; see PreferencesContext. */
  formatQuantity: (value: number | string | null | undefined) => string;
  getChangeCount: (fieldName: string) => number | null;
  canShowWorkflowActions: boolean;
  /** CR-4705: Callback to navigate to change requests tab for review. */
  onReviewChanges?: () => void;
}

export function POLineDetailTabContent({
  line,
  changelogs,
  userType,
  formatCurrency,
  formatDate,
  formatQuantity,
  getChangeCount,
}: POLineDetailTabProps) {
  // CR-7266: the contract this line's prices resolved against, opened from the
  // Contract field below. Held here rather than lifted to POLineDetailModal:
  // the field is the only thing that opens it, and the surrounding modal
  // already stacks its own dialogs this way.
  const [contractModalId, setContractModalId] = React.useState<number | null>(null);
  // CR-7275: one verdict for the whole contract block -- the two price boxes
  // and PPV all read from it, so they cannot drift apart. Empty string means
  // no colour, which is the correct answer for a line with no contract.
  const onContract = hasContract(line);
  const varianceClass = varianceTextClass(getContractPriceVariance(line));
  // A contract part is priced per unit or per lot, never both, so on a
  // unit-priced part the lot box is empty by design -- it says so instead of
  // reading as data that failed to load, and it never takes a colour.
  const lotPriceDisplay = !onContract
    ? NO_CONTRACT_TEXT
    : line.contract_lot_price
      ? formatCurrency(line.contract_lot_price, line.currency)
      : LOT_NOT_APPLICABLE_TEXT;
  // CR-4711 layout:
  //   Row A (header parties): Customer | Supplier Group | Supplier (customer view)
  //                           Customer (supplier view)
  //   Row C: Order Date | Buyer ID                                      (customer-only)
  //   Row D (parts):  Part Number | Part Description | Drawing Issue†
  //   Row E: Batch/Serial No | PnR Note No                              (customer-only)
  //   Row F (qtys):   Qty Ordered | Qty Delivered† | Qty O/S†
  //   Row G (dates):  MRP/ERP Scheduled | Agreed Date (ODD) | Latest Supplier Promise Date
  //   Row H (price):  Unit Price | Currency† | Exchange Rate†
  //   Row I: Total Value | O/S Value                                    (customer-only)
  //   Row J: Contract Unit Price | Contract Lot Price | Contract Price | PPV  (customer-only)
  //   Row K: PDF Attached | Mat Certs                                   (customer-only)
  // † = customer-only — hidden from suppliers via the isCustomer gate.
  const isCustomer = userType === 'customer';
  // CR-4731 round 2: collapse propose+decision pairs so the Audit History
  // shows one row per change-request lifecycle, not two.
  const collapsedChangelogs = useMemo(() => collapseChangeRequestRows(changelogs), [changelogs]);
  /* "Agreed Date (ODD)" is the server's odd_date (backend purchasing/line_odd.py):
     the date the on-time verdict, OTIF and arrears measure this line against.
     At PO-line grain that is original_promise_date, else delivery_date; at
     schedule grain it is delivery_date, the active slot's date.

     It used to be sla_delivery_date || delivery_date (CR-4711). The first is
     empty on every tenant, so this showed delivery_date — which on a feed that
     sends a re-planned due date beside the original one is the re-planned
     date, and it restated "Customer Required Date" below. delivery_date is the
     fallback only for a payload that predates odd_date. */
  const agreedDateOdd = line.odd_date ?? line.delivery_date;

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left Column - Line Details */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Parties row. CR-4711 (QA 13/08): "Customer field: Missing from the
              top section" — it was rendered for supplier users only, so a
              customer viewing the line never saw which of their own sites the
              order belonged to. It now leads the row for both roles. */}
          {userType === 'supplier' && (
            <ReadOnlyField label="Customer" value={line.customer_name} />
          )}
          {isCustomer && (
            <>
              {/* Customer gets its own row rather than a third column.
                  Squeezing three parties into one row truncated all three —
                  the customer name worst, by 154px, because it is
                  site-qualified ("Acme – Automotive – Germany | Stuttgart").
                  It also reads closer to the design: QA's arrow points at the
                  space above Supplier Group, and Sarah's drawing puts the
                  Customer box there rather than inline with the supplier. */}
              <ReadOnlyField label="Customer" value={line.customer_name} />
              <div className={`grid ${line.supplier_group_name ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                {line.supplier_group_name ? (
                  <ReadOnlyField label="Supplier Group" value={line.supplier_group_name} />
                ) : null}
                <ReadOnlyField label="Supplier" value={line.supplier_name} />
              </div>
            </>
          )}

          {/* Order Date + Buyer ID — customer-only */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Order Date" value={line.order_date ? formatDate(line.order_date) : ''} withCalendarIcon />
              <ReadOnlyField label="Buyer ID" value={line.buyer_id} />
            </div>
          )}

          {/* Part identification: Part Number + Description (+ Drawing Issue customer-only) */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
            <ReadOnlyField label="Part Number(s)" value={line.part_number} primaryLabel />
            {/* Customer review 11 Sep (item 30g): a whitespace-only stored description reads as a dash. */}
            <ReadOnlyField label="Part Description" value={line.description?.trim()} />
            {isCustomer && (
              <ReadOnlyField label="Drawing Issue" value={line.drawing_version} />
            )}
          </div>

          {/* Batch/Serial No + PnR Note No — customer-only */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Batch/Serial No" value={line.batch_serial_no} />
              <ReadOnlyField label="PnR Note No" value={line.packing_note_number} />
            </div>
          )}

          {/* Quantities: Ordered always; Delivered + O/S customer-only */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField label="Qty Ordered (Total)" value={formatQuantity(line.quantity)} badge={getChangeCount('quantity')} primaryLabel />
            {isCustomer && (
              <>
                <ReadOnlyField label="Qty Delivered" value={formatQuantity(line.received_qty)} />
                <ReadOnlyField label="Qty O/S" value={formatQuantity(line.outstanding_qty)} />
              </>
            )}
          </div>

          {/* Schedule block. CR-4711 (QA 13/08): three date fields, not six.
              The three labels on Sarah's diagram are arrowed onto the date
              fields that were already on screen — they RENAME those slots.
              Round 6 read them as additions and rendered the new labels
              alongside the originals, which is why QA saw pairs restating one
              another (MRP/ERP Scheduled vs Original Required Date, Agreed Date
              (ODD) vs Customer Required Date, Supplier Promise Date vs Latest
              Supplier Promise Date). The surviving three, per QA's list:
                MRP/ERP Scheduled            → mrp_scheduled_date
                Agreed Date (ODD)            → see agreedDateOdd above
                Latest Supplier Promise Date → supplier_promise_date
              Dropped: Original Required Date, Supplier Promise Date and
              Customer Required Date. The delivery_date change badge goes with
              the last of those; the promise-date badge is kept below.
              None are red-marked on the diagram, so both roles see them. */}
          <div className="grid grid-cols-3 gap-3">
            {/* Customer review 11 Sep (item 27a): only the ERP import
                writes this (MrpDate / MRP_Date / ScheduledDate). A dash here
                is a date the customer's feed never sent, not a lost value. */}
            <div title={line.mrp_scheduled_date ? undefined : "Not supplied by this customer's ERP import"}>
              <ReadOnlyField
                label="MRP/ERP Scheduled"
                value={line.mrp_scheduled_date ? formatDate(line.mrp_scheduled_date) : ''}
                withCalendarIcon
              />
            </div>
            <ReadOnlyField
              label="Agreed Date (ODD)"
              value={agreedDateOdd ? formatDate(agreedDateOdd) : ''}
              withCalendarIcon
            />
            <ReadOnlyField
              label="Latest Supplier Promise Date"
              value={line.supplier_promise_date ? formatDate(line.supplier_promise_date) : ''}
              badge={getChangeCount('supplier_promise_date')}
              withCalendarIcon
            />
          </div>

          {/* Pricing — Unit Price visible to all; Currency + Exchange Rate customer-only */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField
              label="Unit Price"
              value={formatCurrency(line.unit_price, line.currency)}
              badge={getChangeCount('unit_price')}
            />
            {isCustomer && (
              <>
                <ReadOnlyField label="Currency" value={line.currency} />
                {/* Stored as "1 reporting = X PO-ccy"; shown the way buyers
                    read it ("1 USD = ₹94.30"). The raw rate stays in the title.
                    Customer review 11 Sep (item 27b): the title also names
                    which rate this is — see po_line_money.py for authority. */}
                <div title={line.exchange_rate ? `Stored rate: ${line.exchange_rate} (${EXCHANGE_RATE_SOURCE_LABELS[line.exchange_rate_source || ''] || 'source unknown'})` : undefined}>
                  <ReadOnlyField
                    label="Exchange Rate"
                    value={formatExchangeRateDisplay(line.exchange_rate, line.currency, line.converted_currency)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Customer-only commercial block — hidden from suppliers entirely */}
          {isCustomer && (
            <>
              {/* Totals row */}
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  label="Total Value"
                  value={line.total_price ? formatCurrency(line.total_price, line.currency) : ''}
                />
                <ReadOnlyField
                  label="O/S Value"
                  value={line.outstanding_value ? formatCurrency(line.outstanding_value, line.currency) : ''}
                />
              </div>

              {/* CR-7266: which contract the three prices below came from.
                  Given its own row rather than a fifth column: contract
                  numbers are as long as the site-qualified names that forced
                  the same decision for Customer on Row A, and a fifth cell
                  would have truncated all five. Clicking it opens that
                  contract; with no resolved price band the field shows the
                  same dash as every other empty read-only field and is inert.
                  Blank is the normal case -- only lines the PPV backfill has
                  priced carry a band at all. */}
              <div className="grid grid-cols-1 gap-3">
                <ReadOnlyField
                  label="Contract"
                  value={line.contract_number}
                  onValueClick={
                    line.contract_id
                      ? () => setContractModalId(line.contract_id as number)
                      : undefined
                  }
                  valueTitle="Open this contract"
                />
              </div>

              {/* Contract block (4-col row per design).
                  CR-7275: the order price against its contract -- over is red,
                  exact is green, under is amber, and a line with no contract
                  behind it says so rather than going blank, because a blank
                  box reads as a fault. One verdict drives the price boxes and
                  PPV alike; see contractPriceVariance.ts. */}
              <div className="grid grid-cols-4 gap-3">
                <ReadOnlyField
                  label="Contract Unit Price"
                  value={onContract
                    ? (line.contract_unit_price ? formatCurrency(line.contract_unit_price, line.currency) : '')
                    : NO_CONTRACT_TEXT}
                  valueClassName={varianceClass}
                />
                <ReadOnlyField
                  label="Contract Lot Price"
                  value={lotPriceDisplay}
                />
                <ReadOnlyField
                  label="Contract Price"
                  value={onContract
                    ? (line.contract_price ? formatCurrency(line.contract_price, line.currency) : '')
                    : NO_CONTRACT_TEXT}
                  valueClassName={varianceClass}
                />
                <ReadOnlyField label="PPV" value={line.ppv} valueClassName={varianceClass} />
              </div>

              {/* PDF Attached + Mat Certs row. CR-4711 (QA 13/08): "These
                  should be hyperlinks" — each attached document is now a link
                  that downloads it, in place of the bare count. */}
              <div className="grid grid-cols-2 gap-3">
                <DocumentLinksField
                  label="PDF Attached"
                  documents={line.pdf_attached_documents}
                  count={line.pdf_attached_count}
                />
                <DocumentLinksField
                  label="Mat Certs"
                  documents={line.material_cert_documents}
                  count={line.material_cert_count}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Changelog */}
      <div>
        <Changelog entries={collapsedChangelogs} maxHeight="500px" />
      </div>

      {/* CR-7266: the contract behind the Contract field above. Rendered only
          while open so it fetches on demand rather than on every Detail tab. */}
      {contractModalId !== null && (
        <ContractPriceDetailModal
          contractId={contractModalId}
          isOpen
          onClose={() => setContractModalId(null)}
        />
      )}
    </div>
  );
}
